<script setup lang="ts">
import IconShareStroke from '~/assets/icons/event/share-stroke.svg?component'
import IconShareFill from '~/assets/icons/event/share-fill.svg?component'
import WishlistIcon from '~/assets/icons/wishlist.svg?component'
import type { EventDescription } from '~/composables/types/event'
import { parseEventDescriptionToAlgoliaEventItem } from '~/utils/helpers'
const props = defineProps<{
  event: EventDescription
  isShareButtonVisible?: boolean
  isGiftcard?: boolean
}>()

defineEmits<{
  'click:openPreviewModal': []
}>()
const isShareModalOpen = ref(false)
const { isMobile } = useDevice()
const route = useRoute()
const isGiftcard = computed(
  () => props.isGiftcard ?? eventGetters.isGiftcardEvent(props.event)
)
const { t } = useI18n()
const fullLocalizedUrl = useFullLocalizedUrl(true)
const {
  isAlgoliaEventItemOnWishlist,
  addAlgoliaEventItem,
  removeAlgoliaEventItem,
} = useWishlist()

const toggleShareModalOpen = () => {
  isShareModalOpen.value = !isShareModalOpen.value
}
const handleOpenShare = () => {
  if (isMobile && navigator.share) {
    sendGalleryShareIntent(props.event, 'native')
    navigator
      .share({
        title: props.event.title,
        text: isGiftcard.value
          ? t('product.gallery.shareModal.shareGiftcardMessage')
          : t('product.gallery.shareModal.shareEventMessage'),
        url: `${route.path}?utm_source=konfetti&utm_medium=share_event&utm_campaign=mobile`,
      })
      .then(() => {
        sendGalleryShareNativeStatus(props.event, 'success')
      })
      .catch(() => {
        sendGalleryShareNativeStatus(props.event, 'cancelled')
      })
  } else {
    toggleShareModalOpen()
    sendGalleryShareIntent(props.event, 'browser')
  }
}

const shareLink = computed(() =>
  fullLocalizedUrl(
    `${route.path}?${encodeURIComponent(
      'utm_source=konfetti&utm_medium=share_event&utm_campaign=facebook'
    )}`
  )
)

const algoliaEventItem = computed(
  () => parseEventDescriptionToAlgoliaEventItem(props.event) as any
)

const isOnWishlist = computed(() =>
  isAlgoliaEventItemOnWishlist(algoliaEventItem.value)
)

const handleWishlistToggle = () => {
  if (isOnWishlist.value) {
    removeAlgoliaEventItem(algoliaEventItem.value)
  } else {
    addAlgoliaEventItem(algoliaEventItem.value)
  }
}
</script>
<template>
  <div
    class="absolute top-1 md:top-2 right-1 md:right-2 z-1 flex gap-1 md:gap-2 text-xs md:text-base"
  >
    <Button
      color="tertiary"
      class="px-1 md:px-2 py-0.5 overline-text"
      data-test-id="see-all-images-button"
      @click.stop.prevent="$emit('click:openPreviewModal')"
    >
      {{ t('product.seeAllImages') }}
    </Button>
    <Button
      v-if="isShareButtonVisible"
      color="tertiary"
      aria-label="Share"
      data-test-id="share-button"
      class="flex items-center gap-1 px-1 py-0.5 md:px-2 relative group overline-text"
      @click.stop.prevent="handleOpenShare"
    >
      <IconShareFill
        class="w-4 md:w-5 h-4 md:h-5 text-teal-900 transition-opacity duration-200 opacity-0 group-hover:opacity-100"
      />
      <IconShareStroke
        class="w-4 md:w-5 h-4 md:h-5 text-white absolute transition-opacity duration-200 group-hover:opacity-0"
      />
      <span class="hidden md:inline-block">
        {{ t('product.shareButton') }}
      </span>
    </Button>
    <ShareDialog
      :is-share-modal-open="isShareModalOpen"
      :is-giftcard="isGiftcard"
      :page-title="eventGetters.getTitle(event)"
      :link="shareLink"
      @shared="sendGalleryShareOption(event, $event)"
      @close="toggleShareModalOpen"
    />
    <Button
      color="tertiary"
      aria-label="Wishlist"
      data-test-id="wishlist-button"
      class="flex items-center gap-1 px-1 py-0.5 md:px-2 relative group wishlist-button overline-text"
      :class="{ 'wishlist-button--active': isOnWishlist }"
      @click.stop.prevent="handleWishlistToggle"
    >
      <WishlistIcon class="w-5 md:w-6 h-5 md:h-6 mt-0.5" />
      <span class="hidden md:inline-block">
        {{ t('wishlist.productCard.addToWishlistLabel') }}
      </span>
    </Button>
  </div>
</template>

<style lang="postcss" scoped>
@keyframes heartbeat {
  0%,
  40%,
  80%,
  100% {
    transform: scale(1);
  }
  20%,
  60% {
    transform: scale(1.25);
  }
}

.wishlist-button {
  :deep(path) {
    @apply duration-300 ease-in-out;
    fill: transparent;
    @apply stroke-gray-800;
  }

  &:hover :deep(path) {
    @apply stroke-red-500;
  }

  &--active {
    :deep(svg) {
      animation: heartbeat 1s forwards;
      animation-timing-function: ease-in-out;
    }

    :deep(path) {
      @apply fill-red-500 stroke-red-500;
    }
  }

  /* Remove shadow from SVG */
  :deep(svg) {
    filter: none;
  }
}
</style>
